import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { axiosInstance } from "./authSlice";

const baseURL = import.meta.env.VITE_API_BASE_URL;

/* ------------------ FETCH FAQ BY AUDIENCE ------------------ */
export const fetchFaqs = createAsyncThunk(
  "faqs/fetchFaqs",
  async ({ audience, page = 1 }, { rejectWithValue }) => {
    try {
      // const token = getState().auth.token;
      // if (!token) return rejectWithValue("Unauthorized");

      const res = await axiosInstance.get("/admin/faq-list", {
        params: {
          audience,
          page,
        },
      });

      return {
        faq: res.data.data.faq,
        current_page: res.data.data.current_page,
        total_pages: res.data.data.total_pages,
        total_records: res.data.data.total_records,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching FAQ");
    }
  }
);

/* ------------------ ADD OR UPDATE FAQ ------------------ */
export const addOrUpdateFaq = createAsyncThunk(
  "faqs/addOrUpdateFaq",
  async (faqData, { rejectWithValue }) => {
    try {
      const payload = {
        faq_id: faqData.faq_id || undefined, // include only if editing
        question: faqData.question,
        answers: faqData.answers,
        faq_audience: faqData.faq_audience,
        priority: faqData.priority,
      };

      const res = await axiosInstance.post(
        `/admin/add-or-update-faq`,
        payload
      );
      console.log("Faq audience is :", faqData.faq_audience);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error adding/updating FAQ");
    }
  }
);

/* ------------------ DELETE FAQ ------------------ */
export const deleteFaq = createAsyncThunk(
  "faqs/deleteFaq",
  async (faq_id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/delete-faq`, {
        faq_id: faq_id, // required by backend
        is_confirmed: 1, // confirmed by Swal
      });

      return faq_id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error deleting FAQ");
    }
  }
);

/* -----------------------CHECK STATUS ---------------*/

export const statusupdatefaq = createAsyncThunk(
  "faqs/statusupdatefaq",
  async ({ faq_id, is_confirmed }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/faq-status-update`, {
        faq_id,
        is_confirmed: 1,
      });

      //  console.log("The status api returns :", res.data)
      // console.log("Faq id is :", res.data.data.faq_id)
      // console.log("Faq status is :", res.data.data.new_status)
      return {
        faq_id: res.data.data.faq_id,
        status: res.data.data.new_status,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error updating status");
    }
  }
);

/* ---------------------------TAKE PRIORITY ----------*/
export const fetchNextFaqPriority = createAsyncThunk(
  "faqs/fetchNextFaqPriority",
  async (audience, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/get-faq-priority`, {
        audience,
      });

      // Backend returns array of used priorities
      const usedPriorities = res.data.data?.priorities || [];
      // Find next available integer starting from 1
      let nextPriority = 1;
      while (usedPriorities.includes(nextPriority)) nextPriority++;

      return nextPriority;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching priority");
    }
  }
);

/* ------------------ SLICE ------------------ */
const faqSlice = createSlice({
  name: "faqs",
  initialState: {
    list: [],
    loading: false,
    error: null,
    current_page: 1,
    total_pages: 1,
    total_records: 0,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* FETCH */
      .addCase(fetchFaqs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.faq || [];
        state.current_page = action.payload.current_page;
        state.total_pages = action.payload.total_pages;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ADD / UPDATE */
      .addCase(addOrUpdateFaq.fulfilled, (state, action) => {
        // Check what your API returns
        const newFaq = action.payload?.data || action.payload; // safest

        if (!newFaq?.faq_id) return; // avoid adding blank

        const index = state.list.findIndex(
          (item) => item.faq_id === newFaq.faq_id
        );

        if (index !== -1) {
          state.list[index] = newFaq; // update
        } else {
          state.list.unshift(newFaq); // add new
          state.total_records += 1;
        }
      })

      /* DELETE */
      .addCase(deleteFaq.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (item) => item.faq_id !== action.payload
        );
        state.total_records -= 1;
      })

      /* ------- STATUS UPDATE-------- */
      .addCase(statusupdatefaq.fulfilled, (state, action) => {
        const index = state.list.findIndex(
          (item) => item.faq_id === action.payload.faq_id
        );

        if (index !== -1) {
          state.list[index].status = action.payload.new_status; // update FAQ status
        }
      });
  },
});

export default faqSlice.reducer;
